import { createBrowserRouter } from "react-router";
import { lazy, Suspense } from "react";
import ProtectedRoute, { AdminRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import MarketingLayout from "@/layouts/MarketingLayout";
import DashboardLayout from "@/layouts/DashboardLayout";

// Lazy-loaded auth pages
const LandingPage = lazy(() => import("@/pages/auth/LandingPage"));
const SignInPage = lazy(() => import("@/pages/auth/SignInPage"));
const SignUpPage = lazy(() => import("@/pages/auth/SignUpPage"));
const ConfirmationPage = lazy(() => import("@/pages/auth/ConfirmationPage"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);

// Lazy-loaded dashboard pages
const DashboardHome = lazy(() => import("@/pages/dashboard/DashboardHome"));
const AgenciesAndAgents = lazy(
  () => import("@/pages/dashboard/AgenciesAndAgents"),
);
const Carriers = lazy(() => import("@/pages/dashboard/Carriers"));
const AddCarrier = lazy(() => import("@/pages/dashboard/AddCarrier"));
const CarrierPaymentUpload = lazy(
  () => import("@/pages/dashboard/CarrierPaymentUpload"),
);
const Override = lazy(() => import("@/pages/dashboard/Override"));
const PaymentTracking = lazy(() => import("@/pages/dashboard/PaymentTracking"));
const Reports = lazy(() => import("@/pages/dashboard/Reports"));
const SendReport = lazy(() => import("@/pages/dashboard/SendReport"));
const SoldContractUpload = lazy(
  () => import("@/pages/dashboard/SoldContractUpload"),
);
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const AgentExpectedPayment = lazy(
  () => import("@/pages/dashboard/AgentExpectedPayment"),
);
const AddAgent = lazy(() => import("@/pages/dashboard/AddAgent"));
const AddAgency = lazy(() => import("@/pages/dashboard/AddAgency"));
const OverrideHistory = lazy(() => import("@/pages/dashboard/OverrideHistory"));
const FilePayments = lazy(() => import("@/pages/dashboard/FilePayments"));
const FileSoldContracts = lazy(
  () => import("@/pages/dashboard/FileSoldContracts"),
);
const ActivityLog = lazy(() => import("@/pages/dashboard/ActivityLog"));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Marketing layout (public routes)
  {
    element: <MarketingLayout />,
    children: [
      {
        path: "/",
        element: (
          <SuspenseWrapper>
            <LandingPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/sign-in",
        element: (
          <PublicOnlyRoute>
            <SuspenseWrapper>
              <SignInPage />
            </SuspenseWrapper>
          </PublicOnlyRoute>
        ),
      },
      {
        path: "/sign-up",
        element: (
          <PublicOnlyRoute>
            <SuspenseWrapper>
              <SignUpPage />
            </SuspenseWrapper>
          </PublicOnlyRoute>
        ),
      },
      {
        path: "/sign-up/confirmation",
        element: (
          <SuspenseWrapper>
            <ConfirmationPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <SuspenseWrapper>
            <ForgotPasswordPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Dashboard layout (protected routes)
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <DashboardHome />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "agencies-and-agents",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <AgenciesAndAgents />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "agencies-and-agents/add-agency",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <AddAgency />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "agencies-and-agents/add-agent",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <AddAgent />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "carriers",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <Carriers />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "carriers/add-carrier",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <AddCarrier />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "file-upload",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <CarrierPaymentUpload />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "file-upload/payments",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <FilePayments />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "override",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <Override />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "override/history",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <OverrideHistory />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "payment-tracking",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <PaymentTracking />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "reports",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <Reports />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "send-report",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <SendReport />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
      {
        path: "sold-contract-upload",
        element: (
          <SuspenseWrapper>
            <SoldContractUpload />
          </SuspenseWrapper>
        ),
      },
      {
        path: "sold-contract-upload/contracts",
        element: (
          <SuspenseWrapper>
            <FileSoldContracts />
          </SuspenseWrapper>
        ),
      },
      {
        path: "agent-expected-payment",
        element: (
          <SuspenseWrapper>
            <AgentExpectedPayment />
          </SuspenseWrapper>
        ),
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <Settings />
          </SuspenseWrapper>
        ),
      },
      {
        path: "activity-log",
        element: (
          <AdminRoute>
            <SuspenseWrapper>
              <ActivityLog />
            </SuspenseWrapper>
          </AdminRoute>
        ),
      },
    ],
  },
]);
